"use client"

import { useLocale } from "next-intl"
import { useCountry } from "@/components/layout/country-provider"
import { useWaitlist } from "@/components/ui/waitlist-context"
import {
  bookingUrl,
  bookingUrlForCountry,
  membershipBookingUrl,
  BOOKING_PACKAGE_TYPE,
  PRODUCT_IDS,
  type BookingStep,
} from "@/lib/booking"
import { track } from "@/lib/analytics"
import { isBookingOpen, type ProductCategory } from "@/i18n/config"

interface BookingCtaProps {
  children: React.ReactNode
  /** Single base UUID, or `[base, ...addOnIds]` to pre-fill a cart. */
  productId?: string | readonly string[]
  /** Deep-link the booking flow to a specific step (e.g. "addons"). */
  step?: BookingStep
  /** Override the computed booking URL (e.g. membership sign-up flow). */
  overrideHref?: string
  /** Route to the Ahead Membership sign-up flow instead of a product booking. */
  forMembership?: boolean
  /**
   * Product family this CTA books. Drives wave-based launch gating: in a
   * `checkup` market the blood check-up books while `mri` / `membership` open
   * the waitlist modal until the market reaches `launched`. Defaults to
   * `blood`; `forMembership` implies `membership`. Tag MRI CTAs explicitly
   * with `category="mri"`.
   */
  category?: ProductCategory
  /**
   * Force the waitlist-modal branch regardless of `isBookingOpen`. Set by
   * `ServiceBookingCta` when the resolved per-service availability is
   * `coming_soon` / `waitlist` (CMS) even though the category itself is open —
   * so the CTA matches the "join waitlist" label instead of silently booking.
   */
  forceWaitlist?: boolean
  className?: string
  trackingLabel?: string
  trackingLocation?: string
}

export { PRODUCT_IDS }

export function BookingCta({
  children,
  productId,
  step,
  overrideHref,
  forMembership,
  category,
  forceWaitlist,
  className,
  trackingLabel,
  trackingLocation,
}: BookingCtaProps) {
  const locale = useLocale()
  const { code: country } = useCountry()
  const { openWaitlist } = useWaitlist()

  const resolvedCategory: ProductCategory =
    category ?? (forMembership ? "membership" : "blood")

  if (forceWaitlist || !isBookingOpen(country, resolvedCategory)) {
    return (
      <button
        type="button"
        className={className}
        data-testid="booking-cta-root"
        data-tracking-label={trackingLabel}
        data-tracking-location={trackingLocation}
        onClick={() => {
          openWaitlist()
          if (trackingLabel)
            track.ctaClick(
              trackingLabel,
              trackingLocation || "page",
              "waitlist"
            )
        }}
      >
        {children}
      </button>
    )
  }

  // Force the booking app's tab to match this CTA's category — without it the
  // app restores its persisted (or default `mri`) tab, so an MRI CTA can land
  // on blood and vice-versa. See BOOKING_PACKAGE_TYPE.
  const packageType = BOOKING_PACKAGE_TYPE[resolvedCategory]

  const href =
    overrideHref ??
    (forMembership
      ? membershipBookingUrl(locale)
      : resolvedCategory === "blood"
        ? // DE check-up (blood) books via the staging app; every other market
          // delegates to production inside bookingUrlForCountry.
          bookingUrlForCountry(country, locale, productId, step, packageType)
        : bookingUrl(locale, productId, step, packageType))
  return (
    <a
      href={href}
      className={className}
      data-testid="booking-cta-root"
      data-tracking-label={trackingLabel}
      data-tracking-location={trackingLocation}
      onClick={() => {
        if (trackingLabel)
          track.ctaClick(trackingLabel, trackingLocation || "page", href)
      }}
    >
      {children}
    </a>
  )
}
